// assets/header-fs.js
// Header Fruto Salvaje: drawer mobile, búsqueda inline (push), acordeón 2 niveles, predictive search.
// Auto-init por IntersectionObserver (no en load). Sin librerías externas. Listeners passive donde aplica.

(function () {
  'use strict';

  function debounce(fn, wait) {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /* ============================================================
     <header-fs>  — host principal: toggles de búsqueda + drawer
  ============================================================ */
  if (!customElements.get('header-fs')) {
    class HeaderFs extends HTMLElement {
      connectedCallback() {
        this._io = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            this.init();
          },
          { rootMargin: '600px' } // el header siempre está cerca del viewport; margen amplio para init casi inmediata sin bloquear el load
        );
        this._io.observe(this);

        // Fallback: si por alguna razón el IO no dispara (ej. header con display:contents en algún override), garantiza init.
        this._fallback = setTimeout(() => {
          if (!this._initialized) this.init();
        }, 1500);
      }

      init() {
        if (this._initialized) return;
        this._initialized = true;
        clearTimeout(this._fallback);

        this.searchToggles = this.querySelectorAll('[data-fs-toggle-search]');
        this.searchPanel = this.querySelector('[data-fs-search-panel]');
        this.searchInput = this.querySelector('[data-fs-search-input]');
        this.openDrawerBtns = this.querySelectorAll('[data-fs-open-drawer]');
        this.cartLinks = this.querySelectorAll('.header-fs__cart');

        this.searchToggles.forEach((btn) => {
          btn.addEventListener('click', () => this.toggleSearch());
        });

        this.openDrawerBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.openDrawer());
        });

        this.cartLinks.forEach((link) => {
          link.addEventListener('click', (e) => this.onCartClick(e));
        });

        // Cerrar búsqueda con Escape
        this.addEventListener('keyup', (e) => {
          if (e.key === 'Escape' && this.searchPanel?.hasAttribute('data-open')) {
            this.toggleSearch(false);
          }
        });
      }

      onCartClick(e) {
        // Si existe el cart drawer nativo de Dawn (<cart-drawer>) en la página,
        // lo abrimos sin navegar. Si no existe (drawer desactivado / tema sin drawer),
        // dejamos el comportamiento por defecto del enlace (ir a /cart).
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

        e.preventDefault();
        cartDrawer.open();
      }

      toggleSearch(forceOpen) {
        if (!this.searchPanel) return;
        const isOpen = this.searchPanel.hasAttribute('data-open');
        const open = forceOpen !== undefined ? forceOpen : !isOpen;

        if (open) {
          this.searchPanel.hidden = false;
          requestAnimationFrame(() => {
            this.searchPanel.setAttribute('data-open', '');
            this.searchInput?.focus();
          });
        } else {
          this.searchPanel.removeAttribute('data-open');
          const onEnd = () => {
            this.searchPanel.hidden = true;
            this.searchPanel.removeEventListener('transitionend', onEnd);
            clearTimeout(safetyTimeout);
          };
          this.searchPanel.addEventListener('transitionend', onEnd);
          const safetyTimeout = setTimeout(onEnd, 400);
        }

        this.searchToggles.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
      }

      openDrawer() {
        const drawer = this.querySelector('fs-menu-drawer') || document.querySelector('fs-menu-drawer');
        drawer?.open();
      }
    }
    customElements.define('header-fs', HeaderFs);
  }

  /* ============================================================
     <fs-menu-drawer> — drawer lateral mobile, acordeón 2 niveles
  ============================================================ */
  if (!customElements.get('fs-menu-drawer')) {
    class FsMenuDrawer extends HTMLElement {
      connectedCallback() {
        this._io = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            this.init();
          },
          { rootMargin: '600px' }
        );
        this._io.observe(this);

        this._fallback = setTimeout(() => {
          if (!this._initialized) this.init();
        }, 1500);
      }

      init() {
        if (this._initialized) return;
        this._initialized = true;
        clearTimeout(this._fallback);

        this.overlay = document.querySelector('[data-fs-drawer-overlay]');
        this.closeBtns = this.querySelectorAll('[data-fs-close-drawer]');
        this.toggles = this.querySelectorAll('.header-fs__drawer-toggle');

        this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));

        // Cierre por click fuera: delegado a document (no depende de timing del overlay)
        // y respaldado por listener directo en el overlay como segunda capa.
        this._onDocumentClick = (e) => {
          if (!this.hasAttribute('data-open')) return;
          const clickedInsideDrawer = this.contains(e.target);
          const clickedOpenButton = e.target.closest('[data-fs-open-drawer]');
          if (!clickedInsideDrawer && !clickedOpenButton) this.close();
        };
        document.addEventListener('click', this._onDocumentClick, true);

        this.overlay?.addEventListener('click', () => this.close());

        this.toggles.forEach((toggle) => {
          toggle.addEventListener('click', () => this.toggleSection(toggle));
        });

        this.addEventListener('keyup', (e) => {
          if (e.key === 'Escape') this.close();
        });
      }

      toggleSection(toggle) {
        const targetId = toggle.getAttribute('aria-controls');
        const sublist = this.querySelector(`#${targetId}`);
        if (!sublist) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        // Cierra los demás (acordeón de un solo item abierto a la vez)
        this.toggles.forEach((t) => {
          if (t !== toggle) {
            t.setAttribute('aria-expanded', 'false');
            const otherId = t.getAttribute('aria-controls');
            const otherList = this.querySelector(`#${otherId}`);
            if (otherList) {
              otherList.removeAttribute('data-open');
              otherList.hidden = true;
            }
          }
        });

        toggle.setAttribute('aria-expanded', String(!isOpen));
        if (!isOpen) {
          sublist.hidden = false;
          requestAnimationFrame(() => sublist.setAttribute('data-open', ''));
        } else {
          sublist.removeAttribute('data-open');
          sublist.hidden = true;
        }
      }

      open() {
        this.hidden = false;
        if (this.overlay) this.overlay.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        requestAnimationFrame(() => {
          this.setAttribute('data-open', '');
          this.overlay?.setAttribute('data-visible', '');
        });
        const burger = document.querySelector('[data-fs-open-drawer]');
        burger?.setAttribute('aria-expanded', 'true');
        this.querySelector('.header-fs__drawer-close')?.focus();
      }

      close() {
        this.removeAttribute('data-open');
        this.overlay?.removeAttribute('data-visible');
        document.documentElement.style.overflow = '';
        const burger = document.querySelector('[data-fs-open-drawer]');
        burger?.setAttribute('aria-expanded', 'false');

        const onEnd = () => {
          this.hidden = true;
          if (this.overlay) this.overlay.hidden = true;
          this.removeEventListener('transitionend', onEnd);
        };
        this.addEventListener('transitionend', onEnd);
        burger?.focus();
      }
    }
    customElements.define('fs-menu-drawer', FsMenuDrawer);
  }

  /* ============================================================
     <predictive-search-fs> — búsqueda predictiva, solo productos
     Facade: no hace fetch hasta que el usuario escribe (primera interacción real).
  ============================================================ */
  if (!customElements.get('predictive-search-fs')) {
    class PredictiveSearchFs extends HTMLElement {
      connectedCallback() {
        this._io = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            this.init();
          },
          { rootMargin: '600px' }
        );
        this._io.observe(this);

        this._fallback = setTimeout(() => {
          if (!this._initialized) this.init();
        }, 1500);
      }

      init() {
        if (this._initialized) return;
        this._initialized = true;
        clearTimeout(this._fallback);

        this.input = this.querySelector('[data-fs-search-input]');
        this.resultsEl = this.querySelector('[data-fs-search-results]');
        this.loadingText = this.getAttribute('data-loading-text') || 'Buscando…';
        this.abortController = null;

        if (!this.input) return;

        this.input.addEventListener(
          'input',
          debounce((e) => this.onSearch(e.target.value), 300)
        );
      }

      async onSearch(query) {
        const trimmed = query.trim();
        if (trimmed.length < 2) {
          this.resultsEl.hidden = true;
          this.resultsEl.innerHTML = '';
          return;
        }

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.renderLoading();

        try {
          const res = await fetch(
            `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(
              trimmed
            )}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor&section_id=predictive-search-fs`,
            { signal: this.abortController.signal }
          );
          if (!res.ok) {
            this.renderResults([]);
            return;
          }
          const data = await res.json();
          this.renderResults(data?.resources?.results?.products || []);
        } catch (err) {
          if (err.name !== 'AbortError') {
            this.renderResults([]);
          }
        }
      }

      renderLoading() {
        this.resultsEl.hidden = false;
        this.resultsEl.innerHTML = `<p class="header-fs__search-status">${this.escapeHtml(this.loadingText)}</p>`;
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') return '';
        const amount = (cents / 100).toFixed(2);
        // Usa el formato de moneda activo de Shopify si está disponible.
        if (window.Shopify?.formatMoney) {
          try {
            return window.Shopify.formatMoney(cents, window.Shopify.money_format);
          } catch (e) {
            /* fallback abajo */
          }
        }
        return `$${amount}`;
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }

      renderResults(products) {
        if (!products.length) {
          this.resultsEl.innerHTML = '<p class="header-fs__search-status">No encontramos productos con ese nombre.</p>';
          this.resultsEl.hidden = false;
          return;
        }

        const frag = document.createDocumentFragment();
        const list = document.createElement('ul');
        list.className = 'header-fs__search-results-list';
        list.setAttribute('role', 'listbox');

        products.forEach((product) => {
          const li = document.createElement('li');
          li.className = 'header-fs__search-result-item';

          const a = document.createElement('a');
          a.href = product.url;
          a.className = 'header-fs__search-result';
          a.setAttribute('role', 'option');

          const imgWrap = document.createElement('span');
          imgWrap.className = 'header-fs__search-result-img';
          if (product.featured_image?.url || product.image) {
            const img = document.createElement('img');
            img.src = product.featured_image?.url || product.image;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 56;
            img.height = 56;
            imgWrap.appendChild(img);
          }

          const info = document.createElement('span');
          info.className = 'header-fs__search-result-info';

          const title = document.createElement('span');
          title.className = 'header-fs__search-result-title';
          title.textContent = product.title;

          const price = document.createElement('span');
          price.className = 'header-fs__search-result-price';
          const hasDiscount =
            product.compare_at_price_max &&
            parseFloat(product.compare_at_price_max) > parseFloat(product.price_max || product.price);

          if (hasDiscount) {
            const compareEl = document.createElement('s');
            compareEl.className = 'header-fs__search-result-compare';
            compareEl.textContent = this.formatMoney(Math.round(parseFloat(product.compare_at_price_max) * 100));
            price.appendChild(compareEl);
            price.append(' ');
          }
          price.append(this.formatMoney(Math.round(parseFloat(product.price_max || product.price) * 100)));

          info.appendChild(title);
          info.appendChild(price);

          a.appendChild(imgWrap);
          a.appendChild(info);
          li.appendChild(a);
          list.appendChild(li);
        });

        frag.appendChild(list);
        this.resultsEl.innerHTML = '';
        this.resultsEl.appendChild(frag);
        this.resultsEl.hidden = false;
      }
    }
    customElements.define('predictive-search-fs', PredictiveSearchFs);
  }
})();